import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Section = ({ children, className = '' }) => (
  <section className={`mb-12 ${className}`}>{children}</section>
);

const HowItWorksPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="how-it-works-page">
      <Navbar />
      <div className="h-20" />

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#FDF8F4] to-[#F4EDE4] py-16 px-6 md:px-10 lg:px-20">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">How it works</h1>
          <p className="text-lg text-gray-600">Discover unforgettable dining experiences</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 md:px-10 py-14">

        {/* Discover */}
        <Section>
          <h2 className="text-2xl font-bold text-[#D65A31] mb-4">Discover</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            The world is your oyster — and we'll help you find the pearl. MealHoster puts thousands of one-of-a-kind food experiences at your fingertips. Just tell us where you're heading and when, and we'll show you what's cooking.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            A tagine made from a family recipe passed down three generations, served on a hilltop in Morocco. An invite-only supper club tucked away in Dubai. A walking food tour led by a local chef who knows every backstreet spot worth eating at. These aren't restaurants. They're stories. And our hosts can't wait to share theirs with you.
          </p>
          <p className="text-gray-900 font-semibold italic">Come hungry. Leave with a story.</p>
        </Section>

        {/* Book */}
        <Section>
          <h2 className="text-2xl font-bold text-[#D65A31] mb-4">Book</h2>
          <p className="text-gray-700 leading-relaxed">
            Booking is as easy as 1, 2, 3. Find an experience you love, pick your dates, choose how many seats you need, and hit <em>Reserve</em>. That's your part done. Your host gets notified straight away. Once they accept, you pay securely online — no cash, no awkward moments, everything handled through the platform. Changed your mind? No problem. You can cancel your request at any time, even after your host has accepted.
          </p>
        </Section>

        {/* Divider */}
        <hr className="border-gray-200 mb-12" />

        {/* How To Post Food Events */}
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">How to post food events</h2>

        {/* Offer */}
        <Section>
          <h3 className="text-xl font-bold text-[#D65A31] mb-3">Offer</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            You already cook with love. Now cook with purpose. You know that feeling — when someone takes their first bite and their eyes light up. When the table goes quiet for a second because the food is <em>that</em> good. That's not just cooking. That's a gift. MealHoster lets you share it with the world and earn money doing what you already love.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Sign up, tell us a bit about yourself, and create your first experience. Add your photos, set your prices, choose when you're available — and we'll bring the guests to your table. No restaurant needed. No experience required. Just food you're proud of and a story worth sharing.
          </p>
          <p className="text-gray-900 font-semibold italic">Your table. Your rules. Your income.</p>
        </Section>

        {/* Manage */}
        <Section>
          <h3 className="text-xl font-bold text-[#D65A31] mb-3">Manage</h3>
          <p className="text-gray-700 leading-relaxed mb-1 font-medium">Your kitchen. Your dashboard. Everything in one place.</p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Once you're set up, your dashboard gives you a full picture — your experiences, your bookings, your earnings, all at a glance. When a request comes in, you decide. Accept the guests you want, manage your availability, and confirm reservations in a couple of taps. You're always in control of who sits at your table.
          </p>
          <p className="text-gray-700 leading-relaxed">
            We also encourage hosts to message guests before the event. Say hello, share what you're planning to cook, build a bit of anticipation. By the time they arrive, they're not just hungry — they're excited.
          </p>
        </Section>

        {/* Welcome */}
        <Section>
          <h3 className="text-xl font-bold text-[#D65A31] mb-3">Welcome</h3>
          <p className="text-gray-700 leading-relaxed">
            This is what it's all been building to. The table's set, the food's ready, and your guests are on their way. This is your moment. What happens next is the best part — new faces, real conversations, and that look people get when they taste something they weren't expecting. It's not just a meal. It's a dinner party where strangers arrive and friends leave. And the morning after? Your payment lands in full. No chasing, no waiting around. You cook, you host, you get paid.
          </p>
        </Section>

        {/* Divider */}
        <hr className="border-gray-200 mb-12" />

        {/* Our food experiences */}
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">Our food experiences</h2>

        {[
          {
            title: 'Home Cooked / Traditional',
            text: "The kind of meal that takes you back to someone's grandmother's kitchen. Family recipes, generous portions, and flavours that have been perfected over generations. This isn't something you can order on an app. This is someone inviting you into their home and feeding you like you're one of their own.",
          },
          {
            title: 'Food Tour',
            text: "Skip the tourist traps. Walk with a local who actually knows where to eat — the bakery with no sign, the market stall with the queue, the spot only regulars know about. You'll eat more than you planned and discover a side of the city you'd never find on your own.",
          },
          {
            title: 'Aperitivo',
            text: "Small bites, good drinks, and that golden-hour feeling where the evening could go anywhere. Think of it as the Italian art of warming up — light enough to leave you wanting more, social enough to make new friends before the first glass is empty.",
          },
          {
            title: 'Supper Club',
            text: "A chef. A secret menu. A room full of strangers who won't be strangers by dessert. Supper clubs are where food meets theatre — intimate, curated, and impossible to replicate. If you've never been to one, this is your invitation.",
          },
          {
            title: 'Cooking Class',
            text: "Don't just eat the food — learn how to make it. Roll pasta by hand, fold dumplings, nail a curry from scratch. You'll go home with new skills, a full stomach, and a recipe you'll actually use again.",
          },
          {
            title: 'Street Food',
            text: "The best food in the world is served on paper plates. Smoky, messy, loud, and completely unapologetic. Your host knows the stalls, the vendors, and the order to eat everything in. Just bring your appetite and something you don't mind getting sauce on.",
          },
          {
            title: 'BBQ & Grill',
            text: "Fire, smoke, and patience. There's something primal about food cooked over flame — and your host has spent years getting it right. Low and slow or hot and fast, this is outdoor eating at its best. Come hungry, leave happy, smell like charcoal for days.",
          },
          {
            title: 'Sweet Tooth',
            text: "For the people who skip to the dessert menu. Pastries, cakes, chocolate, local sweets you've never heard of — this is a celebration of sugar done properly. Life's short. Eat the good stuff first.",
          },
          {
            title: 'Private Chef',
            text: "Your venue, your guest list, your occasion — their talent. A private chef experience takes the stress off your plate and puts something extraordinary on it. Sit back, pour a glass, and let someone else make you look like an incredible host.",
          },
        ].map((item) => (
          <Section key={item.title}>
            <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
            <p className="text-gray-700 leading-relaxed">{item.text}</p>
          </Section>
        ))}

        {/* CTA */}
        <div className="text-center mt-8">
          <Link
            to="/"
            className="inline-block bg-[#D65A31] hover:bg-[#B84A28] text-white font-semibold px-8 py-3 rounded-lg transition-colors"
            data-testid="explore-btn"
          >
            Explore experiences
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HowItWorksPage;
